import type { TranscriptParagraph, TranscriptSentence } from '@live-stream/shared'

/**
 * The transcript beside the player (Phase 9): which sentence is being
 * spoken, and which sentences say what was searched for.
 */

/** The index of the sentence spoken at `ms`, or the one just before it; -1 before the first. */
export function sentenceAt(sentences: readonly TranscriptSentence[], ms: number): number {
  let lo = 0
  let hi = sentences.length - 1
  let found = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (sentences[mid]!.s <= ms) {
      found = mid
      lo = mid + 1
    } else hi = mid - 1
  }
  return found
}

export type FlatSentence = TranscriptSentence & { paragraph: number }

/** Every sentence in order, with the paragraph it belongs to. */
export function flatten(paragraphs: readonly TranscriptParagraph[]): FlatSentence[] {
  const out: FlatSentence[] = []
  paragraphs.forEach((p, i) => {
    for (const s of p.sentences) out.push({ ...s, paragraph: i })
  })
  return out
}

/** The pieces of `text`, each marked when it matches the query (case-insensitive). */
export function highlight(text: string, query: string): Array<{ text: string; hit: boolean }> {
  const q = query.trim().toLowerCase()
  if (!q) return [{ text, hit: false }]
  const lower = text.toLowerCase()
  const parts: Array<{ text: string; hit: boolean }> = []
  let at = 0
  for (;;) {
    const i = lower.indexOf(q, at)
    if (i < 0) break
    if (i > at) parts.push({ text: text.slice(at, i), hit: false })
    parts.push({ text: text.slice(i, i + q.length), hit: true })
    at = i + q.length
  }
  if (at < text.length) parts.push({ text: text.slice(at), hit: false })
  return parts
}

export const matches = (text: string, query: string): boolean => {
  const q = query.trim().toLowerCase()
  return q === '' || text.toLowerCase().includes(q)
}

/** m:ss, or h:mm:ss past an hour. */
export function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const two = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`
}
